import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';

interface SnackbarContextType {
  showSnackbar: (message: string) => void;
  snackbarOpen: boolean;
  snackbarFading: boolean;
  snackbarMessage: string;
  closeSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) throw new Error('useSnackbar must be used within SnackbarProvider');
  return context;
};

const FADE_DURATION = 500;  // ms de la animación CSS
const VISIBLE_DURATION = 2000; // ms que permanece visible antes de desvanecerse

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarFading, setSnackbarFading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showSnackbar = (message: string) => {
    // Cancelar timers previos si había un toast en curso
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);

    setSnackbarMessage(message);
    setSnackbarFading(false);
    setSnackbarOpen(true);

    // Tras VISIBLE_DURATION, iniciar fade-out
    fadeTimerRef.current = setTimeout(() => {
      setSnackbarFading(true);
      // Tras la duración del fade, ocultar el componente
      closeTimerRef.current = setTimeout(() => {
        setSnackbarOpen(false);
        setSnackbarFading(false);
      }, FADE_DURATION);
    }, VISIBLE_DURATION);
  };

  const closeSnackbar = () => {
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setSnackbarFading(true);
    closeTimerRef.current = setTimeout(() => {
      setSnackbarOpen(false);
      setSnackbarFading(false);
    }, FADE_DURATION);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, snackbarOpen, snackbarFading, snackbarMessage, closeSnackbar }}>
      {children}
    </SnackbarContext.Provider>
  );
};
